import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import config from 'config';
import { findViewport } from 'utils/breakpoints';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { throttle } from 'utils/throttle';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import TreatmentsCard from './TreatmentsCard';
import styles from './Treatments.module.scss';
import type { IBlockTreatmentsCard } from 'types/contentful/generated';

type Props = {
  blockId: string;
  items: IBlockTreatmentsCard[];
  title: string;
  treatmentItems?: {
    items: IBlockTreatmentsCard[];
  };
};

const Treatments = ({
  blockId,
  items: _items,
  title,
  treatmentItems,
}: Props): JSX.Element | null => {
  let items = _items;
  if (treatmentItems) {
    ({ items } = treatmentItems);
  }

  if (!items?.length) {
    return null;
  }

  const containerRef = useRef(null);
  const [curSlide, setCurSlide] = useState(0);
  const [centerMode, setCenterMode] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const { query } = useRouter();
  useVerticalSwipePreventer(containerRef);

  const checkViewport = useCallback((newWidth: number) => {
    const viewportName = findViewport(newWidth);
    if (
      'xl' === viewportName ||
      'lg' === viewportName ||
      'md' === viewportName
    ) {
      setCenterMode(true);
      setIsSmallScreen(false);
    }

    if ('sm' === viewportName || 'xs' === viewportName || '' === viewportName) {
      setCenterMode(false);
      setIsSmallScreen(true);
    }
  }, []);

  useEffect(() => {
    checkViewport(window.innerWidth);

    const throttledResizeListener = throttle(
      () => checkViewport(window.innerWidth),
      200,
    );

    window.addEventListener('resize', throttledResizeListener, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', throttledResizeListener);
    };
  }, []);

  const { treatmentsCardSlider } = config.defaults;
  const speciality = (query.page || '') as string;

  const renderTreatmentCard = (
    item: IBlockTreatmentsCard,
    className: string,
  ): JSX.Element => {
    return (
      <TreatmentsCard
        key={item.sys.id}
        className={className}
        item={item}
        count={items.length}
        speciality={speciality}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div id={sanitizeContentfulBlockId(blockId)} ref={containerRef}>
        {title && <h2>{title}</h2>}
        <div className={styles.wrapper}>
          {items.length > treatmentsCardSlider.threshold ||
          (items.length >= treatmentsCardSlider.threshold && isSmallScreen) ? (
            <Carousel
              responsive={treatmentsCardSlider.responsive}
              arrows
              ssr
              centerMode={centerMode}
              deviceType="desktop"
              partialVisible={!centerMode}
              customLeftArrow={
                <CustomSliderArrow
                  direction="left"
                  className={styles.carouselArrowLeft}
                />
              }
              customRightArrow={
                <CustomSliderArrow
                  direction="right"
                  className={styles.carouselArrowRight}
                />
              }
              removeArrowOnDeviceType={['mobile']}
              containerClass={clsx(
                styles.carouselContainer,
                curSlide > 0
                  ? [styles.showBefore, styles.showAfter]
                  : curSlide < items.length && styles.showAfter,
              )}
              sliderClass={styles.carousel}
              beforeChange={(nextSlide) => {
                setCurSlide(nextSlide);
              }}
            >
              {items.map((item) => {
                return renderTreatmentCard(item, 'h-full');
              })}
            </Carousel>
          ) : (
            <div className="flex">
              {items.map((item) => {
                return renderTreatmentCard(
                  item,
                  clsx(
                    items.length === 1 && isSmallScreen ? 'w-full' : 'w-1/2',
                  ),
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Treatments;
